import { useState, useMemo, Fragment, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  Checkbox,
  Paper,
  TextInput,
  Textarea,
  Divider,
  Alert,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Tabs,
  Menu,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconX,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconStar,
  IconStarFilled,
  IconTrash,
  IconAlertCircle,
  IconInfoCircle,
  IconDotsVertical,
} from "@tabler/icons-react";
import {
  useSurgeryCentersLabsAdmin,
  useCreateSurgeryCenterLab,
  useUpdateSurgeryCenterLab,
  useToggleSurgeryCenterLabActive,
  useSurgeryCenterPhysicians,
  useAddSurgeryCenterPhysician,
  useSetPrimarySurgeryCenterPhysician,
  useRemoveSurgeryCenterPhysician,
  useSurgeryCenterReportDeliveryMethods,
  useAddSurgeryCenterReportDeliveryMethod,
  useUpdateSurgeryCenterReportDeliveryMethod,
  useRemoveSurgeryCenterReportDeliveryMethod,
  useToggleSurgeryCenterReportDeliveryMethodActive,
} from "../../../hooks/admin/useSurgeryCentersLabs";
import { useReportDeliveryMethodsAdmin } from "../../../hooks/admin/useReportDeliveryMethods";
import { PortalAccessSection } from "../../../components/admin/portal-access/PortalAccessSection";
import { AddPortalGrantModal } from "../../../components/admin/portal-access/AddPortalGrantModal";
import { PortalAccessIndicator } from "../../../components/admin/portal-access/PortalAccessIndicator";
import { usePortalAccessSubjectIdSet } from "../../../hooks/admin/usePortalAccess";
import { usePhysiciansAdmin } from "../../../hooks/admin/usePhysicians";
import {
  SurgeryCenterLab,
  CreateSurgeryCenterLabInput,
  CreateSurgeryCenterLabReportDeliveryMethodInput,
  SurgeryCenterLabReportDeliveryMethod,
} from "../../../services/admin/surgeryCentersLabs.service";
import {
  scrollToFirstError,
  required,
  email,
  phone,
  fax,
} from "../../../utils/formValidation";
import SurgeryCenterLabForm, {
  SurgeryCenterLabFormValues,
} from "../../../components/admin/surgeryCentersLabs/SurgeryCenterLabForm";
import { SurgeryCenterLabLogoPreview } from "../../../components/admin/surgeryCentersLabs/SurgeryCenterLabLogoPreview";
import { sanitizeReportFooterHtml } from "../../../utils/sanitize";

interface Filters {
  type: "all" | "surgery_center" | "lab";
  status: "all" | "active" | "inactive";
}

interface DeliveryFormValues {
  reportDeliveryMethodId: string;
  email: string;
  fax: string;
  phone: string;
  address: string;
  hl7EndpointHost: string;
  hl7EndpointPort: number | string;
  hl7FacilityId: string;
  hl7ApplicationId: string;
  remoteConnectionIdentifier: string;
}

const ITEMS_PER_PAGE = 20;

const TYPE_LABELS: Record<string, string> = {
  surgery_center: "Surgery Center",
  lab: "Lab",
};

export default function SurgeryCentersLabsAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const expandedRow = searchParams.get("sc") ?? null;
  const activeTab = searchParams.get("tab") ?? "portal-access";

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Modal states
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    physicianModalOpened,
    { open: openPhysicianModal, close: closePhysicianModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    deliveryModalOpened,
    { open: openDeliveryModal, close: closeDeliveryModal },
  ] = useDisclosure(false);
  const [
    portalGrantModalOpened,
    { open: openPortalGrantModal, close: closePortalGrantModal },
  ] = useDisclosure(false);

  // Page state
  const [editingItem, setEditingItem] = useState<SurgeryCenterLab | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<Filters>({
    type: "all",
    status: "all",
  });
  const [pendingFilters, setPendingFilters] = useState<Filters>({
    type: "all",
    status: "all",
  });
  const [page, setPage] = useState(1);

  // Physician link modal state
  const [selectedPhysicianId, setSelectedPhysicianId] = useState("");
  const [physicianIsPrimary, setPhysicianIsPrimary] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    type: "physician" | "delivery";
    id: string;
    name: string;
  } | null>(null);

  // Delivery method modal state
  const [editingDelivery, setEditingDelivery] =
    useState<SurgeryCenterLabReportDeliveryMethod | null>(null);

  // Data hooks
  const { data: items = [], isLoading, refetch } = useSurgeryCentersLabsAdmin();
  const {
    subjectIdSet: surgeryCentersWithPortal,
    activeIdSet: surgeryCentersWithActivePortal,
  } = usePortalAccessSubjectIdSet("surgery_center");
  const { data: allPhysicians = [] } = usePhysiciansAdmin();
  const { data: deliveryMethods = [] } = useReportDeliveryMethodsAdmin();
  const createItem = useCreateSurgeryCenterLab();
  const updateItem = useUpdateSurgeryCenterLab();
  const toggleActive = useToggleSurgeryCenterLabActive();

  const { data: scPhysicians = [], isLoading: isLoadingPhysicians } =
    useSurgeryCenterPhysicians(expandedRow);

  const duplicatePhysicianNames = useMemo(() => {
    const counts = new Map<string, number>();
    scPhysicians.forEach((p) => {
      const name =
        `${p.physician.firstName} ${p.physician.lastName}`.toLowerCase();
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([name]) => name)
    );
  }, [scPhysicians]);

  const addPhysician = useAddSurgeryCenterPhysician();
  const setPrimary = useSetPrimarySurgeryCenterPhysician();
  const removePhysician = useRemoveSurgeryCenterPhysician();

  // Delivery method hooks
  const { data: scDeliveryMethods = [], isLoading: isLoadingDelivery } =
    useSurgeryCenterReportDeliveryMethods(expandedRow);
  const addDeliveryMethod = useAddSurgeryCenterReportDeliveryMethod();
  const updateDeliveryMethod = useUpdateSurgeryCenterReportDeliveryMethod();
  const removeDeliveryMethod = useRemoveSurgeryCenterReportDeliveryMethod();
  const toggleDeliveryActive =
    useToggleSurgeryCenterReportDeliveryMethodActive();

  // Delivery method form
  const deliveryForm = useForm<DeliveryFormValues>({
    initialValues: {
      reportDeliveryMethodId: "",
      email: "",
      fax: "",
      phone: "",
      address: "",
      hl7EndpointHost: "",
      hl7EndpointPort: "",
      hl7FacilityId: "",
      hl7ApplicationId: "",
      remoteConnectionIdentifier: "",
    },
    validate: {
      reportDeliveryMethodId: required("Delivery method is required"),
      email: email(),
      fax: fax(),
      phone: phone(),
      remoteConnectionIdentifier: (value, values) => {
        const method = deliveryMethods.find(
          (m) => m.id === values.reportDeliveryMethodId
        );
        if (
          method?.name?.toLowerCase().includes("remote print") &&
          !value?.trim()
        ) {
          return "Connection identifier is required for Remote Print delivery";
        }
        return null;
      },
    },
  });

  // Filter items
  const filteredItems = useMemo(() => {
    let result = items;
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.externalCaseNumberPrefix?.toLowerCase().includes(query)
      );
    }
    if (filters.type !== "all") {
      result = result.filter((item) => item.type === filters.type);
    }
    if (filters.status === "active") {
      result = result.filter((item) => item.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((item) => !item.isActive);
    }
    return result;
  }, [items, debouncedSearch, filters]);

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, page]);

  const hasActiveFilters = filters.type !== "all" || filters.status !== "all";

  const linkedPhysicianIds = scPhysicians.map((p) => p.physicianId);
  const availablePhysicians = allPhysicians.filter(
    (p) => p.isActive && !linkedPhysicianIds.includes(p.id)
  );

  // Handlers
  const handleAdd = () => {
    setEditingItem(null);
    openModal();
  };

  const handleEdit = (item: SurgeryCenterLab) => {
    setEditingItem(item);
    openModal();
  };

  const handleFormSubmit = async (values: SurgeryCenterLabFormValues) => {
    const input: CreateSurgeryCenterLabInput = {
      name: values.name,
      type: values.type,
      address: values.address || null,
      primaryPhone: values.primaryPhone || null,
      secondaryPhone: values.secondaryPhone || null,
      fax: values.fax || null,
      medicalDirector: values.medicalDirector?.trim() || null,
      footerHtml: sanitizeReportFooterHtml(values.footerHtml) || null,
      externalCaseNumberPrefix: values.externalCaseNumberPrefix || null,
      preferredPathologistId: values.preferredPathologistId || null,
      logoS3Key: values.logoS3Key || null,
    };
    if (editingItem) {
      await updateItem.mutateAsync({ id: editingItem.id, ...input });
    } else {
      await createItem.mutateAsync(input);
    }
    closeModal();
  };

  const handleToggleActive = async (item: SurgeryCenterLab) => {
    await toggleActive.mutateAsync({ id: item.id, isActive: !item.isActive });
  };

  const toggleRow = (scId: string) => {
    if (expandedRow === scId) {
      updateParams({ sc: null, tab: null });
    } else {
      updateParams({ sc: scId, tab: null });
    }
  };

  const handleOpenPhysicianModal = () => {
    setSelectedPhysicianId("");
    setPhysicianIsPrimary(false);
    openPhysicianModal();
  };

  const handleAddPhysician = async () => {
    if (!expandedRow || !selectedPhysicianId) return;
    await addPhysician.mutateAsync({
      surgeryCenterLabId: expandedRow,
      input: {
        physicianId: selectedPhysicianId,
        isPrimary: physicianIsPrimary,
      },
    });
    closePhysicianModal();
  };

  const handleSetPrimary = async (physicianId: string) => {
    if (!expandedRow) return;
    await setPrimary.mutateAsync({
      surgeryCenterLabId: expandedRow,
      physicianId,
    });
  };

  const handleConfirmDelete = (
    type: "physician" | "delivery",
    id: string,
    name: string
  ) => {
    setItemToDelete({ type, id, name });
    openDeleteModal();
  };

  const handleDelete = async () => {
    if (!expandedRow || !itemToDelete) return;
    if (itemToDelete.type === "physician") {
      await removePhysician.mutateAsync({
        surgeryCenterLabId: expandedRow,
        physicianId: itemToDelete.id,
      });
    } else {
      await removeDeliveryMethod.mutateAsync({
        surgeryCenterLabId: expandedRow,
        id: itemToDelete.id,
      });
    }
    closeDeleteModal();
    setItemToDelete(null);
  };

  // Delivery method handlers
  const handleOpenDeliveryModal = (
    delivery?: SurgeryCenterLabReportDeliveryMethod
  ) => {
    if (delivery) {
      setEditingDelivery(delivery);
      deliveryForm.setValues({
        reportDeliveryMethodId: delivery.reportDeliveryMethodId,
        email: delivery.email || "",
        fax: delivery.fax || "",
        phone: delivery.phone || "",
        address: delivery.address || "",
        hl7EndpointHost: delivery.hl7EndpointHost || "",
        hl7EndpointPort: delivery.hl7EndpointPort || "",
        hl7FacilityId: delivery.hl7FacilityId || "",
        hl7ApplicationId: delivery.hl7ApplicationId || "",
        remoteConnectionIdentifier: delivery.remoteConnectionIdentifier || "",
      });
    } else {
      setEditingDelivery(null);
      // Prefill from the current location's contact info
      const current = items.find((i) => i.id === expandedRow);
      deliveryForm.setValues({
        reportDeliveryMethodId: "",
        email: "",
        fax: current?.fax || "",
        phone: current?.primaryPhone || "",
        address: current?.address || "",
        hl7EndpointHost: "",
        hl7EndpointPort: "",
        hl7FacilityId: "",
        hl7ApplicationId: "",
        remoteConnectionIdentifier: "",
      });
    }
    openDeliveryModal();
  };

  const handleDeliverySubmit = deliveryForm.onSubmit(
    async (values) => {
      if (!expandedRow) return;

      const input: CreateSurgeryCenterLabReportDeliveryMethodInput = {
        reportDeliveryMethodId: values.reportDeliveryMethodId,
        email: values.email || null,
        fax: values.fax || null,
        phone: values.phone || null,
        address: values.address || null,
        hl7EndpointHost: values.hl7EndpointHost?.trim() || null,
        hl7EndpointPort:
          typeof values.hl7EndpointPort === "number"
            ? values.hl7EndpointPort
            : null,
        hl7FacilityId: values.hl7FacilityId?.trim() || null,
        hl7ApplicationId: values.hl7ApplicationId?.trim() || null,
        remoteConnectionIdentifier:
          values.remoteConnectionIdentifier?.trim() || null,
      };

      try {
        if (editingDelivery) {
          await updateDeliveryMethod.mutateAsync({
            surgeryCenterLabId: expandedRow,
            input: { id: editingDelivery.id, ...input },
          });
        } else {
          await addDeliveryMethod.mutateAsync({
            surgeryCenterLabId: expandedRow,
            input,
          });
        }
        closeDeliveryModal();
      } catch {
        // Portal-without-access is prevented in the UI (see portalNeedsSetup),
        // so submission can't reach the backend's NO_PORTAL_ACCESS guard. Any
        // other failure is surfaced by the mutation's onError toast.
      }
    },
    () => scrollToFirstError(deliveryForm)
  );

  const handleCloseDeliveryModal = useCallback(() => {
    closeDeliveryModal();
  }, [closeDeliveryModal]);

  const handleOpenPortalGrantModal = useCallback(() => {
    openPortalGrantModal();
  }, [openPortalGrantModal]);

  const handleClosePortalGrantModal = useCallback(() => {
    closePortalGrantModal();
  }, [closePortalGrantModal]);

  const handleToggleDeliveryActive = async (id: string) => {
    if (!expandedRow) return;
    await toggleDeliveryActive.mutateAsync({
      surgeryCenterLabId: expandedRow,
      id,
    });
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const defaultFilters: Filters = { type: "all", status: "all" };

  const handleClearAllFilters = () => {
    setFilters(defaultFilters);
    setPendingFilters(defaultFilters);
    setPage(1);
  };

  // The selected delivery method drives which contact fields the modal shows.
  const selectedMethod = deliveryMethods.find(
    (m) => m.id === deliveryForm.values.reportDeliveryMethodId
  );
  const methodName = selectedMethod?.name?.toLowerCase() || "";

  // Portal delivery needs at least one eligible portal grant on the location.
  // Surface the warning (and suppress the submit button) the moment Portal is
  // chosen and none exists — no need to attempt a save first.
  const portalNeedsSetup =
    methodName.startsWith("portal") &&
    !!expandedRow &&
    !surgeryCentersWithPortal.has(expandedRow);

  const portalIsPending =
    methodName.startsWith("portal") &&
    !!expandedRow &&
    surgeryCentersWithPortal.has(expandedRow) &&
    !surgeryCentersWithActivePortal.has(expandedRow);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Surgery Centers / Labs</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage surgery centers and labs and their linked physicians
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add Surgery Center/Lab
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or case number prefix..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.type !== "all" && (
                      <Badge variant="light" color="grape">
                        {TYPE_LABELS[filters.type]}
                      </Badge>
                    )}
                    {filters.status !== "all" && (
                      <Badge variant="light" color="blue">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredItems.length} item
                  {filteredItems.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Surgery Centers / Labs ({filteredItems.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredItems.length)} of{" "}
                      {filteredItems.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredItems.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No items match the current filters"
                    : "No surgery centers or labs found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }} />
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Address</Table.Th>
                      <Table.Th style={{ width: 140 }}>Primary Phone</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Fragment key={item.id}>
                        <Table.Tr>
                          {/* Expand toggle */}
                          <Table.Td>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => toggleRow(item.id)}
                            >
                              {expandedRow === item.id ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                            </ActionIcon>
                          </Table.Td>

                          <Table.Td>
                            <Group gap="xs" wrap="nowrap" align="center">
                              <PortalAccessIndicator
                                hasAccess={surgeryCentersWithPortal.has(
                                  item.id
                                )}
                                subjectLabel={item.name}
                              />
                              <Text size="sm" fw={500}>
                                {item.name}
                              </Text>
                            </Group>
                          </Table.Td>

                          <Table.Td>
                            <Text size="sm" lineClamp={1}>
                              {item.address || "—"}
                            </Text>
                          </Table.Td>

                          <Table.Td>
                            <Text size="sm">{item.primaryPhone || "—"}</Text>
                          </Table.Td>

                          <Table.Td>
                            <Badge
                              color={item.isActive ? "green" : "red"}
                              variant="light"
                            >
                              {item.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </Table.Td>

                          <Table.Td>
                            <Menu
                              position="bottom-end"
                              offset={4}
                              withinPortal
                              shadow="md"
                            >
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label="Row actions"
                                >
                                  <IconDotsVertical size={18} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={<IconEdit size={16} />}
                                  onClick={() => handleEdit(item)}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={
                                    item.isActive ? (
                                      <IconX size={16} />
                                    ) : (
                                      <IconCheck size={16} />
                                    )
                                  }
                                  color={item.isActive ? "red" : "green"}
                                  onClick={() => void handleToggleActive(item)}
                                >
                                  {item.isActive ? "Deactivate" : "Activate"}
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Table.Td>
                        </Table.Tr>

                        {/* Expanded row */}
                        {expandedRow === item.id && (
                          <Table.Tr>
                            <Table.Td colSpan={6} p={0}>
                              <Box pl="xl" pr="md" pb="md" pt="sm" bg="gray.0">
                                {/* Logo / name detail strip */}
                                <Group
                                  align="center"
                                  gap="md"
                                  mb="md"
                                  pb="sm"
                                  style={{
                                    borderBottom:
                                      "1px solid var(--mantine-color-gray-3)",
                                  }}
                                >
                                  <Box
                                    style={{
                                      width: 80,
                                      height: 80,
                                      flexShrink: 0,
                                      border:
                                        "2px solid var(--mantine-color-gcpBlue-2)",
                                      borderRadius: 12,
                                      overflow: "hidden",
                                      background: "#fff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <SurgeryCenterLabLogoPreview
                                      logoS3Key={item.logoS3Key ?? null}
                                      name={item.name}
                                    />
                                  </Box>
                                  <Stack gap={2} style={{ minWidth: 0 }}>
                                    <Text fw={600}>{item.name}</Text>
                                    <Text size="xs" c="dimmed">
                                      {item.logoS3Key
                                        ? "Logo appears in the report header for pass-through cases."
                                        : "No logo uploaded. Edit to add one."}
                                    </Text>
                                  </Stack>
                                </Group>

                                <Tabs
                                  value={activeTab}
                                  onChange={(tab) =>
                                    updateParams({
                                      tab:
                                        tab && tab !== "portal-access"
                                          ? tab
                                          : null,
                                    })
                                  }
                                >
                                  <Tabs.List>
                                    <Tabs.Tab value="portal-access">
                                      Portal Access
                                    </Tabs.Tab>
                                    <Tabs.Tab value="physicians">
                                      Physicians ({scPhysicians.length})
                                    </Tabs.Tab>
                                    <Tabs.Tab value="report-delivery">
                                      Report Delivery (
                                      {scDeliveryMethods.length})
                                    </Tabs.Tab>
                                  </Tabs.List>

                                  {/* Portal Access Tab */}
                                  <Tabs.Panel value="portal-access" pt="md">
                                    <PortalAccessSection
                                      subjectType="surgery_center"
                                      subjectId={item.id}
                                      subjectName={item.name}
                                    />
                                  </Tabs.Panel>

                                  <Tabs.Panel value="physicians" pt="md">
                                    {duplicatePhysicianNames.size > 0 && (
                                      <Alert
                                        icon={<IconAlertCircle size={16} />}
                                        color="orange"
                                        variant="light"
                                        mb="md"
                                        title="Potential duplicate physicians detected"
                                      >
                                        <Text size="sm">
                                          Multiple physician records with the
                                          same name are linked to this location.
                                          Please review these records and ensure
                                          they refer to distinct individuals;
                                          identical duplicates can cause issues
                                          with reporting and billing.
                                        </Text>
                                      </Alert>
                                    )}
                                    <Group justify="flex-end" mb="sm">
                                      <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={handleOpenPhysicianModal}
                                        disabled={
                                          availablePhysicians.length === 0
                                        }
                                      >
                                        Link Physician
                                      </Button>
                                    </Group>
                                    {isLoadingPhysicians ? (
                                      <Center py="md">
                                        <Loader size="sm" />
                                      </Center>
                                    ) : scPhysicians.length === 0 ? (
                                      <Text size="sm" c="dimmed">
                                        No physicians linked to this location.
                                      </Text>
                                    ) : (
                                      <Table>
                                        <Table.Thead>
                                          <Table.Tr>
                                            <Table.Th>Physician</Table.Th>
                                            <Table.Th>NPI</Table.Th>
                                            <Table.Th>Email</Table.Th>
                                            <Table.Th style={{ width: 80 }} />
                                          </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                          {scPhysicians.map((scp) => (
                                            <Table.Tr key={scp.id}>
                                              <Table.Td>
                                                <Group gap="xs">
                                                  <Text size="sm" fw={500}>
                                                    {scp.physician.firstName}{" "}
                                                    {scp.physician.lastName}
                                                  </Text>
                                                  {duplicatePhysicianNames.has(
                                                    `${scp.physician.firstName} ${scp.physician.lastName}`.toLowerCase()
                                                  ) && (
                                                    <Tooltip label="Multiple records with this name are linked to this location">
                                                      <IconAlertCircle
                                                        size={14}
                                                        color="var(--mantine-color-orange-6)"
                                                      />
                                                    </Tooltip>
                                                  )}
                                                  {scp.isPrimary && (
                                                    <Badge
                                                      size="xs"
                                                      variant="light"
                                                      color="blue"
                                                    >
                                                      Primary
                                                    </Badge>
                                                  )}
                                                </Group>
                                              </Table.Td>
                                              <Table.Td>
                                                <Text size="sm" c="dimmed">
                                                  {scp.physician.npi || "—"}
                                                </Text>
                                              </Table.Td>
                                              <Table.Td>
                                                <Text size="sm" c="dimmed">
                                                  {scp.physician.email || "—"}
                                                </Text>
                                              </Table.Td>
                                              <Table.Td>
                                                <Group gap="xs">
                                                  {!scp.isPrimary && (
                                                    <Tooltip label="Set as Primary">
                                                      <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        onClick={() =>
                                                          handleSetPrimary(
                                                            scp.physicianId
                                                          )
                                                        }
                                                      >
                                                        <IconStar size={14} />
                                                      </ActionIcon>
                                                    </Tooltip>
                                                  )}
                                                  {scp.isPrimary && (
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      color="yellow"
                                                      disabled
                                                    >
                                                      <IconStarFilled
                                                        size={14}
                                                      />
                                                    </ActionIcon>
                                                  )}
                                                  <Tooltip label="Unlink">
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      color="red"
                                                      onClick={() =>
                                                        handleConfirmDelete(
                                                          "physician",
                                                          scp.physicianId,
                                                          `${scp.physician.firstName} ${scp.physician.lastName}`
                                                        )
                                                      }
                                                    >
                                                      <IconTrash size={14} />
                                                    </ActionIcon>
                                                  </Tooltip>
                                                </Group>
                                              </Table.Td>
                                            </Table.Tr>
                                          ))}
                                        </Table.Tbody>
                                      </Table>
                                    )}
                                  </Tabs.Panel>

                                  {/* Report Delivery Tab */}
                                  <Tabs.Panel value="report-delivery" pt="md">
                                    <Group justify="flex-end" mb="sm">
                                      <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={() =>
                                          handleOpenDeliveryModal()
                                        }
                                      >
                                        Add Delivery Method
                                      </Button>
                                    </Group>
                                    {isLoadingDelivery ? (
                                      <Center py="md">
                                        <Loader size="sm" />
                                      </Center>
                                    ) : scDeliveryMethods.length === 0 ? (
                                      <Text size="sm" c="dimmed">
                                        No delivery methods configured.
                                      </Text>
                                    ) : (
                                      <Stack gap="sm">
                                        {scDeliveryMethods.map((dm) => {
                                          const dmMethodName =
                                            dm.reportDeliveryMethod?.name?.toLowerCase() ||
                                            "";
                                          const isHl7 =
                                            dmMethodName.startsWith("hl7");
                                          const isPortal =
                                            dmMethodName.startsWith("portal");
                                          const isEmail =
                                            dmMethodName.startsWith("email");
                                          const isFax =
                                            dmMethodName.startsWith("fax");
                                          const isShip =
                                            dmMethodName.startsWith("ship") ||
                                            dmMethodName === "mail";
                                          const isRemotePrint =
                                            dmMethodName.includes(
                                              "remote print"
                                            );

                                          const destination = isHl7
                                            ? [
                                                dm.hl7EndpointHost &&
                                                dm.hl7EndpointPort
                                                  ? `${dm.hl7EndpointHost}:${dm.hl7EndpointPort}`
                                                  : dm.hl7EndpointHost || "",
                                                dm.hl7FacilityId
                                                  ? `facility: ${dm.hl7FacilityId}`
                                                  : "",
                                                dm.hl7ApplicationId
                                                  ? `app: ${dm.hl7ApplicationId}`
                                                  : "",
                                              ]
                                                .filter(Boolean)
                                                .join(" · ")
                                            : isPortal || isEmail
                                            ? dm.email || ""
                                            : isFax
                                            ? dm.fax || ""
                                            : isRemotePrint
                                            ? dm.remoteConnectionIdentifier ||
                                              ""
                                            : isShip
                                            ? [dm.address || "", dm.phone || ""]
                                                .filter(Boolean)
                                                .join(" · ")
                                            : dm.fax ||
                                              dm.email ||
                                              dm.address ||
                                              "";

                                          return (
                                            <Group
                                              key={dm.id}
                                              justify="space-between"
                                            >
                                              <Group
                                                gap="xs"
                                                style={{ minWidth: 0, flex: 1 }}
                                              >
                                                <Text
                                                  size="sm"
                                                  fw={500}
                                                  style={{ flexShrink: 0 }}
                                                >
                                                  {
                                                    dm.reportDeliveryMethod
                                                      ?.name
                                                  }
                                                </Text>
                                                {destination && (
                                                  <Text
                                                    size="xs"
                                                    c="dimmed"
                                                    truncate="end"
                                                    style={{
                                                      minWidth: 0,
                                                      overflow: "hidden",
                                                    }}
                                                  >
                                                    {destination}
                                                  </Text>
                                                )}
                                              </Group>
                                              <Group gap="xs">
                                                <Badge
                                                  size="xs"
                                                  color={
                                                    dm.isActive
                                                      ? "green"
                                                      : "red"
                                                  }
                                                  variant="light"
                                                >
                                                  {dm.isActive
                                                    ? "Active"
                                                    : "Inactive"}
                                                </Badge>
                                                <Tooltip label="Edit">
                                                  <ActionIcon
                                                    size="sm"
                                                    variant="subtle"
                                                    onClick={() =>
                                                      handleOpenDeliveryModal(
                                                        dm
                                                      )
                                                    }
                                                  >
                                                    <IconEdit size={14} />
                                                  </ActionIcon>
                                                </Tooltip>
                                                <Tooltip
                                                  label={
                                                    dm.isActive
                                                      ? "Deactivate"
                                                      : "Activate"
                                                  }
                                                >
                                                  <ActionIcon
                                                    size="sm"
                                                    variant="subtle"
                                                    color={
                                                      dm.isActive
                                                        ? "red"
                                                        : "green"
                                                    }
                                                    onClick={() =>
                                                      void handleToggleDeliveryActive(
                                                        dm.id
                                                      )
                                                    }
                                                  >
                                                    {dm.isActive ? (
                                                      <IconX size={14} />
                                                    ) : (
                                                      <IconCheck size={14} />
                                                    )}
                                                  </ActionIcon>
                                                </Tooltip>
                                                <Tooltip label="Remove">
                                                  <ActionIcon
                                                    size="sm"
                                                    variant="subtle"
                                                    color="red"
                                                    onClick={() =>
                                                      handleConfirmDelete(
                                                        "delivery",
                                                        dm.id,
                                                        dm.reportDeliveryMethod
                                                          ?.name || ""
                                                      )
                                                    }
                                                  >
                                                    <IconTrash size={14} />
                                                  </ActionIcon>
                                                </Tooltip>
                                              </Group>
                                            </Group>
                                          );
                                        })}
                                      </Stack>
                                    )}
                                  </Tabs.Panel>
                                </Tabs>
                              </Box>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Fragment>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Modal */}
        <Modal.Root
          opened={modalOpened}
          onClose={closeModal}
          size="xl"
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingItem
                  ? "Edit Surgery Center/Lab"
                  : "Add Surgery Center/Lab"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <SurgeryCenterLabForm
                key={editingItem?.id || "new"}
                formId="surgery-center-lab-form"
                hideFooter
                initialValues={editingItem || undefined}
                onSubmit={handleFormSubmit}
              />
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="surgery-center-lab-form"
                loading={createItem.isPending || updateItem.isPending}
              >
                {editingItem ? "Update" : "Create"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Link Physician Modal */}
        <Modal
          opened={physicianModalOpened}
          onClose={closePhysicianModal}
          title="Link Physician"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Physician"
              placeholder="Select a physician..."
              data={availablePhysicians.map((p) => ({
                value: p.id,
                label: `${p.firstName} ${p.lastName}${
                  p.npi ? ` (NPI: ${p.npi})` : ""
                }`,
              }))}
              value={selectedPhysicianId}
              onChange={(value) => setSelectedPhysicianId(value || "")}
              searchable
              withAsterisk
            />
            <Checkbox
              label="Set as primary physician for this location"
              checked={physicianIsPrimary}
              onChange={(e) => setPhysicianIsPrimary(e.currentTarget.checked)}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closePhysicianModal}>
                Cancel
              </Button>
              <Button
                loading={addPhysician.isPending}
                onClick={() => {
                  if (!selectedPhysicianId) {
                    notifications.show({
                      color: "orange",
                      message: "Please select a physician",
                    });
                    return;
                  }
                  void handleAddPhysician();
                }}
              >
                Link Physician
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Confirm Removal"
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              {itemToDelete?.type === "delivery" ? (
                <>
                  Are you sure you want to remove the{" "}
                  <strong>{itemToDelete?.name}</strong> delivery method from
                  this location?
                </>
              ) : (
                <>
                  Are you sure you want to unlink{" "}
                  <strong>{itemToDelete?.name}</strong> from this location?
                </>
              )}
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={() => void handleDelete()}
                loading={
                  removePhysician.isPending || removeDeliveryMethod.isPending
                }
              >
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Add/Edit Delivery Method Modal */}
        <Modal.Root
          opened={deliveryModalOpened}
          onClose={handleCloseDeliveryModal}
          size="md"
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingDelivery
                  ? "Edit Delivery Method"
                  : "Add Delivery Method"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <form id="sc-delivery-form" onSubmit={handleDeliverySubmit}>
                <Stack gap="md">
                  <Select
                    label="Delivery Method"
                    placeholder="Select delivery method..."
                    data={deliveryMethods
                      .filter(
                        (m) =>
                          m.isActive && !m.name?.toLowerCase().startsWith("hl7")
                      )
                      .map((m) => ({
                        value: m.id,
                        label: m.name,
                      }))}
                    withAsterisk
                    {...deliveryForm.getInputProps("reportDeliveryMethodId")}
                  />

                  {portalIsPending && (
                    <Alert
                      icon={<IconInfoCircle size={16} />}
                      color="blue"
                      variant="light"
                      title="Portal invitation pending"
                      role="status"
                    >
                      <Text size="sm">
                        This location has portal contacts, but none have
                        activated their accounts yet. Reports will be available
                        in the portal once a contact completes registration.
                      </Text>
                    </Alert>
                  )}

                  {portalNeedsSetup && (
                    <Alert
                      icon={<IconAlertCircle size={16} />}
                      color="yellow"
                      variant="light"
                      title="Set up portal access first"
                      role="alert"
                    >
                      <Stack gap="sm">
                        <Text size="sm">
                          This location has no active or invited portal access
                          grants yet. Add at least one portal contact so the
                          Portal delivery method has a recipient to route to.
                        </Text>
                        <Group>
                          <Button
                            size="xs"
                            variant="filled"
                            color="yellow"
                            onClick={handleOpenPortalGrantModal}
                          >
                            Set up portal access
                          </Button>
                        </Group>
                      </Stack>
                    </Alert>
                  )}

                  {methodName === "email" && (
                    <TextInput
                      label="Email Address"
                      placeholder="Enter email address"
                      {...deliveryForm.getInputProps("email")}
                    />
                  )}

                  {methodName === "fax" && (
                    <TextInput
                      label="Fax Number"
                      placeholder="Enter fax number"
                      {...deliveryForm.getInputProps("fax")}
                    />
                  )}

                  {(methodName.startsWith("ship") || methodName === "mail") && (
                    <Textarea
                      label="Mailing Address / Shipping Address"
                      placeholder="Enter full address"
                      autosize
                      minRows={2}
                      maxRows={4}
                      {...deliveryForm.getInputProps("address")}
                    />
                  )}

                  {methodName.includes("remote print") && (
                    <TextInput
                      label="Connection Identifier"
                      placeholder="e.g. clinic-printer-001"
                      description="Must match the identifier in the Remote Print client app at the clinic"
                      withAsterisk
                      {...deliveryForm.getInputProps(
                        "remoteConnectionIdentifier"
                      )}
                    />
                  )}

                  {methodName.startsWith("portal") && (
                    <Text size="sm" c="dimmed">
                      Reports will be routed to this location's portal. Manage
                      portal contacts in the Portal Access tab.
                    </Text>
                  )}

                  {(methodName.startsWith("ship") || methodName === "mail") && (
                    <TextInput
                      label="Phone (optional)"
                      placeholder="Enter phone number"
                      {...deliveryForm.getInputProps("phone")}
                    />
                  )}
                </Stack>
              </form>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={handleCloseDeliveryModal}>
                Cancel
              </Button>
              {!portalNeedsSetup && (
                <Button
                  type="submit"
                  form="sc-delivery-form"
                  loading={
                    addDeliveryMethod.isPending ||
                    updateDeliveryMethod.isPending
                  }
                >
                  {editingDelivery ? "Update" : "Add"}
                </Button>
              )}
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Portal Access setup — opened from the delivery-method modal when the
            admin tries to use Portal but the location has no active grants */}
        {expandedRow && (
          <AddPortalGrantModal
            opened={portalGrantModalOpened}
            onClose={handleClosePortalGrantModal}
            subjectType="surgery_center"
            subjectId={expandedRow}
            subjectName={
              items.find((i) => i.id === expandedRow)?.name || "This location"
            }
            mode="primary"
            primaryDefaults={{
              email: "",
              firstName: "",
              lastName: "",
              role: "Contact Person",
            }}
          />
        )}

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Surgery Centers / Labs"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Type"
              placeholder="Select type..."
              data={[
                { value: "all", label: "All Types" },
                { value: "surgery_center", label: "Surgery Centers" },
                { value: "lab", label: "Labs" },
              ]}
              value={pendingFilters.type}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  type: (value as Filters["type"]) || "all",
                })
              }
            />
            <Select
              label="Status"
              placeholder="Select status..."
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.status}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: (value as Filters["status"]) || "all",
                })
              }
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => setPendingFilters(defaultFilters)}
              >
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
